// 兼容性 hook：结合 React Query 和 Zustand store
import { useQueryClient } from '@tanstack/react-query';
import {
  useAuthStore,
  useAuthUser,
  useUserProfile,
  useSignInMutation,
  useSignUpMutation,
  useSignOutMutation,
} from '@ext-bookmark/shared/auth';

export const useAuth = () => {
  const store = useAuthStore();
  const queryClient = useQueryClient();
  const { data: user, isLoading: isLoadingUser, error: userError } = useAuthUser();
  const { data: profile, isLoading: isLoadingProfile } = useUserProfile(user?.id);
  const signInMutation = useSignInMutation();
  const signUpMutation = useSignUpMutation();
  const signOutMutation = useSignOutMutation();

  const loading =
    isLoadingUser ||
    isLoadingProfile ||
    signInMutation.isPending ||
    signUpMutation.isPending ||
    signOutMutation.isPending;
  const error =
    userError?.message ||
    signInMutation.error?.message ||
    signUpMutation.error?.message ||
    signOutMutation.error?.message ||
    store.error ||
    null;

  return {
    user: user || store.user,
    profile: profile || store.profile,
    loading,
    error,
    signUp: async (email: string, password: string, username?: string) => {
      try {
        await signUpMutation.mutateAsync({ email, password, username });
      } catch (err: any) {
        store.setError(err.message || '注册失败');
        throw err;
      }
    },
    signIn: async (email: string, password: string) => {
      try {
        await signInMutation.mutateAsync({ email, password });
      } catch (err: any) {
        store.setError(err.message || '登录失败');
        throw err;
      }
    },
    signOut: async () => {
      try {
        await signOutMutation.mutateAsync();
      } catch (err: any) {
        store.setError(err.message || '登出失败');
        throw err;
      }
    },
    refreshProfile: async () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ['auth', 'profile', user.id] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['auth', 'user'] });
      }
    },
  };
};
